import React, { useState, useEffect } from 'react';
import { Car, MapPin } from 'lucide-react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Import new components from Fleet directory
import DashboardHomeContainer from '../components/DashboardHome/DashboardHome.container';
import GeofenceContainer from '../components/Geofence/Geofence.container';
// Import the new device details page
import DeviceFullDetailsPage from '../components/DashboardHome/DeviceFullDetailsPage';
// Import the new geofence edit page
import GeofenceEditPage from '../components/Geofence/GeofenceEditPage';

export default function FleetView() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = window.innerWidth < 768;
  
  // Determine active tab based on URL
  const isGeofenceTab = location.pathname.includes('/geofence');
  const [activeTab, setActiveTab] = useState(isGeofenceTab ? 'geofence' : 'devices');
  
  // Handle direct navigation to /dashboard/fleet
  useEffect(() => {
    // If we're at exactly /dashboard/fleet without a sub-route, redirect to devices
    if (location.pathname === '/dashboard/fleet') {
      navigate('/dashboard/fleet/devices', { replace: true });
    }
  }, [location.pathname, navigate]);
  
  // Update tab value when location changes
  useEffect(() => {
    if (location.pathname.includes('/geofence')) {
      setActiveTab('geofence');
    } else if (location.pathname.includes('/devices')) {
      setActiveTab('devices');
    }
  }, [location.pathname]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    // Navigate to the appropriate route
    if (value === 'devices') {
      navigate('/dashboard/fleet/devices');
    } else {
      navigate('/dashboard/fleet/geofence');
    }
  };

  const tabs = [
    {
      id: 'devices',
      label: 'Devices',
      icon: Car,
      path: 'devices'
    },
    {
      id: 'geofence',
      label: 'Geofence',
      icon: MapPin,
      path: 'geofence'
    }
  ];

  return (
    <div className={cn(
      "relative flex flex-col"
    )}>
      <Card className="p-4 rounded-none border-b border-gray-200 bg-white">
        <div className="container mx-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Fleet Management
          </h1>
          <p className="text-gray-600">
            Monitor and manage your vehicle fleet and geofences
          </p>
        </div>
      </Card>

      <div className="flex-1 flex flex-col">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="h-full flex flex-col">
          <div className="border-b border-gray-200 bg-white">
            <div className="container mx-auto px-4">
              <TabsList className="grid w-full grid-cols-2 bg-transparent h-auto p-0">
                {tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className={cn(
                        "flex flex-col items-center gap-2 py-4 px-2 min-h-16 text-sm font-medium",
                        "data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600",
                        "data-[state=active]:border-b-2 data-[state=active]:border-blue-600",
                        isMobile && "text-xs px-1"
                      )}
                    >
                      <IconComponent className="w-5 h-5" />
                      <span className={cn(
                        "text-center leading-tight",
                        isMobile && "text-xs"
                      )}>
                        {tab.label}
                      </span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>
          </div>

          <div className="flex-1">
            <Routes>
              <Route path="devices" element={
                <TabsContent value="devices" className="m-0 h-full flex flex-col">
                  <DashboardHomeContainer />
                </TabsContent>
              } />
              <Route path="devices/:deviceId" element={
                <TabsContent value="devices" className="m-0 h-full">
                  <DeviceFullDetailsPage />
                </TabsContent>
              } />

              {/* Geofence list view (main geofence tab) */}
              <Route path="geofence" element={ 
                <TabsContent value="geofence" className="m-0 h-full flex flex-col">
                  <GeofenceContainer />
                </TabsContent>
              } />
              {/* New routes for creating and editing geofences */}
              <Route path="geofence/new" element={
                <TabsContent value="geofence" className="m-0 h-full"> {/* Keep in geofence tab context */}
                  <GeofenceEditPage />
                </TabsContent>
              } />
              <Route path="geofence/edit/:geofenceId" element={
                <TabsContent value="geofence" className="m-0 h-full"> {/* Keep in geofence tab context */}
                  <GeofenceEditPage />
                </TabsContent>
              } />

              <Route path="*" element={<Navigate to="devices" replace />} />
            </Routes>
          </div>
        </Tabs>
      </div>
    </div>
  );
} 